import { addDays, startOfDay } from 'date-fns';
import { useMemo } from 'react';
import { getEventInTimezone } from '../../hooks';
import type { CalendarEvent } from '../../types';

const compareEvents = <T extends CalendarEvent>(leftEvent: T, rightEvent: T) => {
  const isLeftAllDay = leftEvent.isAllDay === true;
  const isRightAllDay = rightEvent.isAllDay === true;

  if (isLeftAllDay !== isRightAllDay) {
    return isLeftAllDay ? -1 : 1;
  }

  const startDifference = leftEvent.start.valueOf() - rightEvent.start.valueOf();
  if (startDifference !== 0) {
    return startDifference;
  }

  return leftEvent.end.valueOf() - rightEvent.end.valueOf();
};

type Properties<T extends CalendarEvent> = {
  days: Date[];
  events?: T[];
  primaryTimezone: string;
};

export const useGetEventsByDay = <T extends CalendarEvent>({ days, events, primaryTimezone }: Properties<T>) =>
  useMemo(() => {
    const eventMap = new Map<string, T[]>();
    const dayKeyByTimestamp = new Map<number, string>();
    const eventsInTimezone = events?.map((event) => getEventInTimezone<T>(event, primaryTimezone)) ?? [];

    for (const day of days) {
      const dayStart = startOfDay(day);
      const dayKey = day.toISOString();

      eventMap.set(dayKey, []);
      dayKeyByTimestamp.set(dayStart.getTime(), dayKey);
    }

    if (days.length === 0 || eventsInTimezone.length === 0) {
      return eventMap;
    }

    const firstDay = days.at(0);
    const lastDay = days.at(-1);

    if (!firstDay || !lastDay) {
      return eventMap;
    }

    const firstDayStart = startOfDay(firstDay);
    const lastDayStart = startOfDay(lastDay);
    const visibleIntervalEnd = addDays(lastDayStart, 1);

    for (const event of eventsInTimezone) {
      if (event.end <= firstDayStart || event.start >= visibleIntervalEnd) {
        continue;
      }

      const eventStart = startOfDay(new Date(Math.max(event.start.getTime(), firstDayStart.getTime())));
      const eventEnd = new Date(Math.min(event.end.getTime(), visibleIntervalEnd.getTime()));

      for (let day = eventStart; day < eventEnd; day = addDays(day, 1)) {
        const dayKey = dayKeyByTimestamp.get(day.getTime());

        if (dayKey) {
          eventMap.get(dayKey)?.push(event);
        }
      }
    }

    for (const [dayKey, dayEvents] of eventMap) {
      eventMap.set(dayKey, dayEvents.toSorted(compareEvents));
    }

    return eventMap;
  }, [days, events, primaryTimezone]);
